import { useCallback, useEffect, useState } from 'react'
import {
  byUrgency,
  loadLetters,
  segmentOf,
  type LetterTask,
  type Segment,
} from '@core/letters'
import type { FamilySession } from '@core/session'
import { Screen, Spinner } from '@design/components'
import { DocumentViewer } from '@features/documents/DocumentViewer'
import { LetterCard } from './LetterCard'

/**
 * docs/spec.md §15 — three segments, and Archive is a filter inside ALL rather
 * than a fourth tab.
 *
 * Rule 23: sections hide when empty. An empty segment shows one honest line
 * instead of a "0 items" row, and the segment buttons only carry a count when
 * there is something to count.
 */
const SEGMENTS: { id: Segment; label: string }[] = [
  { id: 'needs_you', label: 'Nodig jou' },
  { id: 'waiting', label: 'Wachten' },
  { id: 'all', label: 'Alles' },
]

export function LettersTab({ session }: { session: FamilySession }) {
  const [tasks, setTasks] = useState<LetterTask[] | null>(null)
  const [segment, setSegment] = useState<Segment>('needs_you')
  const [viewing, setViewing] = useState<string | null>(null)
  const [error, setError] = useState('')

  const refresh = useCallback(async () => {
    try {
      setTasks(await loadLetters())
    } catch {
      setError('Kon de brieven niet laden.')
      setTasks([])
    }
  }, [])

  useEffect(() => {
    void refresh()
  }, [refresh])

  if (tasks === null) return <Spinner label="Even geduld…" />

  const counts = {
    needs_you: tasks.filter((t) => segmentOf(t) === 'needs_you').length,
    waiting: tasks.filter((t) => segmentOf(t) === 'waiting').length,
  }

  const shown = tasks
    .filter((t) => (segment === 'all' ? true : segmentOf(t) === segment))
    .sort(byUrgency)

  return (
    <Screen className="flex flex-col">
      <h1 className="text-2xl font-semibold mb-3">Brieven</h1>

      <div className="flex gap-1 p-1 rounded-xl bg-surface mb-4">
        {SEGMENTS.map((s) => (
          <button
            key={s.id}
            onClick={() => setSegment(s.id)}
            className={`flex-1 py-2 rounded-lg text-sm transition-colors ${
              segment === s.id ? 'bg-canvas font-medium' : 'text-muted'
            }`}
          >
            {s.label}
            {s.id !== 'all' && counts[s.id as 'needs_you' | 'waiting'] > 0 && (
              <span className="ml-1.5 text-xs">{counts[s.id as 'needs_you' | 'waiting']}</span>
            )}
          </button>
        ))}
      </div>

      {error && <p className="text-danger text-sm mb-3">{error}</p>}

      <div className="flex-1 scroll-area">
        {shown.length === 0 ? (
          <EmptyState segment={segment} />
        ) : (
          shown.map((t) => (
            <LetterCard
              key={t.id}
              task={t}
              userId={session.userId}
              onView={setViewing}
              onChanged={() => void refresh()}
            />
          ))
        )}
      </div>

      {viewing && <DocumentViewer documentId={viewing} onClose={() => setViewing(null)} />}
    </Screen>
  )
}

function EmptyState({ segment }: { segment: Segment }) {
  const text =
    segment === 'needs_you'
      ? 'Niets dat op jou wacht.'
      : segment === 'waiting'
        ? 'Je wacht nergens op.'
        : 'Nog geen brieven.'
  return <p className="text-muted text-center py-12">{text}</p>
}
